import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Footer from './Footer';
import useAnalytics from '../../hooks/useAnalytics';
import {
  CONSENT_UPDATED_EVENT,
  type ConsentPreferences,
  type ConsentRecord,
  getConsent,
  setConsent,
} from '../../privacy/consent';
import ThemeProvider from '../../theme/ThemeProvider';
import ConsentBanner from '../Privacy/ConsentBanner';
import ConsentModal from '../Privacy/ConsentModal';
import ThemeToggle from '../Theme/ThemeToggle';

type AppShellProps = React.PropsWithChildren;

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { t } = useTranslation('layout');
  const [consent, setConsentState] = useState<ConsentRecord | null>(null);
  const [isBannerVisible, setBannerVisible] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  useAnalytics();

  const currentPreferences = useMemo<ConsentPreferences>(
    () => ({
      analytics: consent?.categories.analytics ?? false,
      marketing: consent?.categories.marketing ?? false,
    }),
    [consent],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const existingConsent = getConsent();

    if (existingConsent) {
      setConsentState(existingConsent);
      setBannerVisible(false);
    } else {
      setBannerVisible(true);
    }

    const handleConsentUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ consent: ConsentRecord | null }>;
      const nextConsent = customEvent.detail?.consent ?? null;

      setConsentState(nextConsent);
      setBannerVisible(!nextConsent);
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);

    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);
    };
  }, []);

  const handleAcceptAll = useCallback(() => {
    const updatedConsent = setConsent({ analytics: true, marketing: true });
    setConsentState(updatedConsent);
    setBannerVisible(false);
    setModalOpen(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const updatedConsent = setConsent({ analytics: false, marketing: false });
    setConsentState(updatedConsent);
    setBannerVisible(false);
    setModalOpen(false);
  }, []);

  const handleCustomize = useCallback(() => {
    setModalOpen(true);
    setBannerVisible(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    if (!consent) {
      setBannerVisible(true);
    }
  }, [consent]);

  const handleSavePreferences = useCallback((preferences: ConsentPreferences) => {
    const updatedConsent = setConsent(preferences);
    setConsentState(updatedConsent);
    setBannerVisible(false);
    setModalOpen(false);
  }, []);

  const handleOpenPrivacySettings = useCallback(() => {
    setModalOpen(true);
    setBannerVisible(false);
  }, []);

  return (
    <ThemeProvider>
      <div className="app-shell">
        <a href="#main" className="app-shell__skip-link">
          {t('skipToContent')}
        </a>
        <header className="app-shell__topbar" role="banner">
          <div className="container app-shell__topbar-inner">
            <ThemeToggle />
          </div>
        </header>
        <main id="main" className="app-shell__main" tabIndex={-1} role="main">
          {children}
        </main>
        <Footer onOpenPrivacySettings={handleOpenPrivacySettings} />
        <ConsentBanner
          isVisible={isBannerVisible}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onCustomize={handleCustomize}
        />
        <ConsentModal
          isOpen={isModalOpen}
          preferences={currentPreferences}
          onClose={handleCloseModal}
          onSave={handleSavePreferences}
        />
      </div>
    </ThemeProvider>
  );
};

export default AppShell;
